'use client';

import { AutoComplete } from '@/components/ui/autocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { keepPreviousData } from '@tanstack/react-query';
import { Book, Clock, Loader2, Mail, MapPin, Phone, Plus, ShoppingCart, StickyNote, Terminal, Trash2, User, AlertTriangle, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { AppointmentBuilder } from '~/components/appointments/AppointmentBuilder';
import AppointmentCartModal from '~/components/appointments/AppointmentCartModal';
import { CANCELLATION_REASON_ICONS, CANCELLATION_REASON_OPTIONS, MOVE_REASON_OPTIONS, MOVE_REASONS_REQUIRING_NOTE } from '~/components/appointments/cancellationReasons';
import { AppointmentCancellationReason } from '@server/prisma-generated/enums';
import { CustomerAppointmentList } from '~/components/customer/new/CustomerAppointmentsList';
import CreateCustomerModal from '~/components/treatment/appointment/drawer/CreateCustomerModal';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '~/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Separator } from '~/components/ui/separator';
import { Switch } from '~/components/ui/switch';
import LocalDateTime, { type IANATimezone } from '~/lib/localdatetime';
import { getDiscountCodeWarnings, formatDiscountCodeWarningMessage } from '~/lib/discount-code-warnings';
import { isDiscountValidForStore } from '~/lib/discount-code-store';
import { LogsTable } from '~/pages/Logs';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { Customer, useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import { AppointmentStatus, getAppointmentStatusLabel } from '~/types/server-types/AppointmentStatus';

const LATE_THRESHOLD_HOURS = 48;

type RebookFromAppointment = {
    reasonCode: AppointmentCancellationReason;
    note: string;
};

/** Whether the appointment (originally starting at `startDateTime`, "yyyy-MM-dd HH:mm" in `timezone`) starts in under 48h. */
const isWithinLateThreshold = (startDateTime: string | null, timezone: IANATimezone) => {
    if (!startDateTime) {
        return false;
    }
    const hoursUntilStart = (new LocalDateTime(startDateTime, timezone).toUTC().getTime() - Date.now()) / (60 * 60 * 1000);
    return hoursUntilStart < LATE_THRESHOLD_HOURS;
};

const OTHER_REASON_WARNING =
    'Nur in Ausnahmefällen verwenden, wenn keiner der anderen Gründe auch nur annähernd zutrifft oder wenn der Grund nicht bekannt ist.';

const SelectedBookableItemsList = () => {
    const { selectedBookableItems, steps, startTime } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            selectedBookableItems: state.selectedBookableItems,
            steps: state.steps,
            startTime: state.startTime,
        }))
    );

    const formatOffsetToClock = useCallback((offsetMinutes: number) => {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const baseMinutes = (startHours || 0) * 60 + (startMinutes || 0);
        const normalized = ((baseMinutes + offsetMinutes) % 1440 + 1440) % 1440;
        const hours = Math.floor(normalized / 60).toString().padStart(2, '0');
        const minutes = (normalized % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }, [startTime]);

    return (
        <div className='flex flex-wrap gap-2'>
            {selectedBookableItems.map((item) => {
                const itemSteps = steps.filter((step) => item.stepIds.includes(step.id));
                const timeRange = itemSteps.length > 0
                    ? {
                        start: Math.min(...itemSteps.map((s) => s.minuteStart)),
                        end: Math.max(...itemSteps.map((s) => s.minuteEnd)),
                    }
                    : null;

                return (
                    <div
                        key={item.bookableItemId}
                        className='inline-flex flex-col items-start rounded-full px-2.5 py-0.5 text-xs font-semibold border'
                        style={{
                            backgroundColor: `${item.color || '#6366F1'}20`,
                            color: item.color || '#6366F1',
                            borderColor: item.color || '#6366F1'
                        }}
                    >
                        <span>
                            {item.bookableItemName}
                            {item.bookableItemType === 'BUNDLE' && ' (Paket)'}
                            {item.addOns.length > 0 && ` (+${item.addOns.length})`}
                        </span>
                        {timeRange && (
                            <span className='font-normal text-[10px]'>
                                {formatOffsetToClock(timeRange.start)} - {formatOffsetToClock(timeRange.end)} Uhr
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    )
}

const appointmentColorPresets = [
    "#FFFC92", // bright yellow (Influencer)
    "#E1E97B", // yellow-green (Constantin)
    "#BDD7A5", // (Doctors)
    "#DEC8E2", // (Medical Skin Experts)
    "#B5CFE8", // (Digital Service)
    "#BBDACB", // (Attached Appointments)
    // "#D94518", // (Appointments to be moved)
    "#fca16f", // (Appointments to be moved) (old color)
    "#FBE4B2", // (Appointment Blocker)
    // "#A80F0F", // (Cancellation) (old color)
    "#fc626f", // (Cancellation)
    "#B3FCF3", // (Pending)
    "#FC03DF", // CO2 Laser Treatments
];

const AppointmentColorPicker = () => {
    const { color, setColor } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            color: state.color,
            setColor: state.setColor,
        }))
    );

    return (
        <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium">Farbe auswählen</label>
            <div className="flex items-center space-x-3">
                <div className="flex items-center">
                    <Button
                        className={`h-8 w-8 rounded-full p-0`}
                        style={{ backgroundColor: color }}
                        variant='default'
                        onClick={() => { }}
                    />
                </div>

                <div className="flex items-center space-x-2 border-l pl-3">
                    {appointmentColorPresets.map((presetColor) => (
                        <Button
                            key={presetColor}
                            className={`h-8 w-8 rounded-full p-0 ${color === presetColor ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                            style={{ backgroundColor: presetColor }}
                            variant={color === presetColor ? "default" : "outline"}
                            onClick={() => setColor(presetColor)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const CreateCustomerButtonAndModal = () => {
    const { setCustomer } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            setCustomer: state.setCustomer,
        }))
    )

    const [open, setOpen] = useState<boolean>(false);

    const handleCustomerCreated = (customer: Customer) => {
        setCustomer(customer);
    }

    return (
        <React.Fragment>
            <Button
                variant='outline'
                onClick={() => setOpen(true)}
            >
                <Plus />
                Neuer Kunde
            </Button>

            <CreateCustomerModal
                open={open}
                onClose={() => setOpen(false)}
                onCustomerCreated={(customer) => handleCustomerCreated(customer)}
            />
        </React.Fragment>
    )
}

type AppointmentLogsModalProps = {
    appointmentId: string;
    open: boolean;
    onClose: () => void;
};

function AppointmentLogsModal({ appointmentId, open, onClose }: AppointmentLogsModalProps) {
    const logsQuery = trpc.logs.getLogs.useQuery(
        {
            page: 1,
            pageSize: 100,
            searchQuery: appointmentId,
        },
        {
            placeholderData: keepPreviousData,
            enabled: open === true,
        }
    );

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Termin-Logs</DialogTitle>
                    <DialogDescription>
                        Alle System-Logs für diesen Termin (ID: <span className="font-mono">{appointmentId}</span>)
                    </DialogDescription>
                </DialogHeader>
                <div className="p-4">
                    {logsQuery.isError && (
                        <Alert variant="destructive" className="mb-6">
                            <Terminal className="h-4 w-4" />
                            <AlertTitle>Fehler beim Laden der Logs</AlertTitle>
                            <AlertDescription>
                                {logsQuery.error?.message ?? "Ein unbekannter Fehler ist aufgetreten."}
                            </AlertDescription>
                        </Alert>
                    )}
                    <div className="rounded-md border bg-muted/50 p-2 max-h-[50vh] overflow-auto">
                        <LogsTable
                            logs={logsQuery.data?.logs ?? []}
                            isLoading={logsQuery.isLoading}
                            itemsPerPage={100}
                        />
                        {logsQuery.data?.logs?.length === 0 && !logsQuery.isLoading && (
                            <div className="text-center text-muted-foreground py-8">
                                Keine Logs für diesen Termin gefunden.
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Schließen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

type ConfirmDeclinedAppointmentButtonProps = {
    onSubmit: () => void | Promise<void>
}
const ConfirmDeclinedAppointmentButton = ({ onSubmit }: ConfirmDeclinedAppointmentButtonProps) => {
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button>
                    Speichern
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Statusänderung bestätigen</AlertDialogTitle>
                    <AlertDialogDescription>
                        Bist du dir sicher, dass du den Termin auf "Abgelehnt" setzen möchtest?
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                        asChild
                    >
                        <CreateOrModifyAppointmentFormSubmitButton
                            onSuccess={async () => {
                                await onSubmit();
                            }}
                        />
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}


const isReasonNoteFilled = (note: string) => note.trim().length > 0;

const formatStartForDisplay = (localDatetime: string) =>
    new LocalDateTime(localDatetime, 'Europe/Berlin').format('dd.MM.yyyy HH:mm');

type ReasonSelectProps = {
    label: string;
    options: Array<[AppointmentCancellationReason, string]>;
    value: AppointmentCancellationReason | undefined;
    onChange: (value: AppointmentCancellationReason) => void;
};
const ReasonSelect = ({ label, options, value, onChange }: ReasonSelectProps) => {
    return (
        <div className="space-y-2">
            <label className="text-sm font-medium">{label}</label>
            <Select value={value} onValueChange={(val) => onChange(val as AppointmentCancellationReason)}>
                <SelectTrigger className="h-auto min-h-9 w-full whitespace-normal text-left *:data-[slot=select-value]:line-clamp-none">
                    <SelectValue placeholder="Grund auswählen..." />
                </SelectTrigger>
                <SelectContent className="max-h-[min(40rem,var(--radix-select-content-available-height))] max-w-[var(--radix-select-trigger-width)]">
                    {options.map(([optionValue, optionLabel]) => {
                        const Icon = CANCELLATION_REASON_ICONS[optionValue];
                        const isOther = optionValue === AppointmentCancellationReason.OTHER;
                        return (
                            <SelectItem key={optionValue} value={optionValue}>
                                <span className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <span className="flex flex-col">
                                        <span className="flex items-center gap-2">
                                            {optionLabel}
                                            {isOther && (
                                                <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />
                                            )}
                                        </span>
                                        {isOther && (
                                            <span className="text-xs text-red-700 whitespace-normal [[data-slot=select-trigger]_&]:hidden">
                                                {OTHER_REASON_WARNING}
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
            {value === AppointmentCancellationReason.OTHER && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <p className="text-sm text-red-800">{OTHER_REASON_WARNING}</p>
                </div>
            )}
        </div>
    );
};

type ReasonNoteFieldProps = {
    value: string;
    onChange: (value: string) => void;
    required: boolean;
    requiredPlaceholder: string;
    optionalPlaceholder: string;
};
const ReasonNoteField = ({ value, onChange, required, requiredPlaceholder, optionalPlaceholder }: ReasonNoteFieldProps) => {
    return (
        <div className="space-y-2">
            <label className="text-sm font-medium">
                {required ? 'Bitte Grund angeben*' : 'Anmerkung (optional)'}
            </label>
            <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={required ? requiredPlaceholder : optionalPlaceholder}
                rows={3}
            />
        </div>
    );
};

type ConfirmMoveAppointmentButtonProps = {
    previousStart: string;
    newStart: string;
    isLateMove: boolean;
    reason: AppointmentCancellationReason | undefined;
    onReasonChange: (reason: AppointmentCancellationReason) => void;
    note: string;
    onNoteChange: (note: string) => void;
    onBeforeSubmit: () => boolean;
    onBeforeRebookSubmit: () => boolean;
    getDetectedWarnings: () => string[];
    onSubmit: () => void | Promise<void>;
};
const ConfirmMoveAppointmentButton = ({
    previousStart,
    newStart,
    isLateMove,
    reason,
    onReasonChange,
    note,
    onNoteChange,
    onBeforeSubmit,
    onBeforeRebookSubmit,
    getDetectedWarnings,
    onSubmit,
}: ConfirmMoveAppointmentButtonProps) => {
    const { customer, steps, selectedBookableItems } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            customer: state.customer,
            steps: state.steps,
            selectedBookableItems: state.selectedBookableItems,
        }))
    );

    const isNoteRequired = !!reason && MOVE_REASONS_REQUIRING_NOTE.includes(reason);
    const isNoteValid = !isNoteRequired || isReasonNoteFilled(note);
    const canConfirm = !!reason && isNoteValid;

    const hasLegacyItems = selectedBookableItems.some((item) => item.type === 'legacy');
    const canRebook = canConfirm && !hasLegacyItems && reason !== AppointmentCancellationReason.CANCELLED_BY_CLINIC;

    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button disabled={!customer || steps.length === 0}>
                    Speichern
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-2xl">
                <AlertDialogHeader>
                    <AlertDialogTitle>Verschiebung bestätigen (Grundabfrage)</AlertDialogTitle>
                    <AlertDialogDescription>
                        Der Termin wird von {previousStart} auf {newStart} verschoben. Bitte gib den Grund an.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="min-w-0 space-y-4">
                    {isLateMove && (
                        <div className="flex items-start gap-2 rounded-md border border-red-400 bg-red-100 p-3 text-sm text-red-800">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <p className="min-w-0">
                                Der Termin beginnt in weniger als {LATE_THRESHOLD_HOURS} Stunden. Die Frist für eine Verschiebung
                                ist abgelaufen, daher sollte der Termin nicht verschoben werden. Bitte sage den Termin stattdessen
                                ab und buche einen neuen. Für den abgesagten Termin kann eine Ausfallgebühr erhoben werden. In Ausnahmen (z.B. Verschiebung um wenige Minuten oder Verschiebung wegen Kalia) kann der Termin trotzdem verschoben werden.
                            </p>
                        </div>
                    )}

                    <ReasonSelect
                        label="Grund für die Verschiebung*"
                        options={MOVE_REASON_OPTIONS}
                        value={reason}
                        onChange={onReasonChange}
                    />

                    <ReasonNoteField
                        value={note}
                        onChange={onNoteChange}
                        required={isNoteRequired}
                        requiredPlaceholder="Grund für die Verschiebung eingeben..."
                        optionalPlaceholder="Weitere Details zur Verschiebung..."
                    />

                    {isLateMove && hasLegacyItems && (
                        <p className="text-sm text-muted-foreground">
                            "Absagen und neu buchen" ist für Termine mit Alt-Leistungen nicht möglich.
                        </p>
                    )}
                </div>
                <AlertDialogFooter className="sm:flex-wrap">
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                        asChild
                        disabled={!canConfirm}
                    >
                        <CreateOrModifyAppointmentFormSubmitButton
                            canSubmit={() => canConfirm}
                            onBeforeSubmit={onBeforeSubmit}
                            getDetectedWarnings={getDetectedWarnings}
                            cancellationReason={reason}
                            cancellationNote={note}
                            onSuccess={async () => {
                                await onSubmit();
                            }}
                            {...(isLateMove && {
                                customButtonStyle: 'bg-red-600 hover:bg-red-700 text-white',
                                customButtonContent: 'Trotzdem umbuchen',
                            })}
                        />
                    </AlertDialogAction>
                    {isLateMove && (
                        <AlertDialogAction
                            asChild
                            disabled={!canRebook}
                        >
                            <CreateOrModifyAppointmentFormSubmitButton
                                canSubmit={() => canRebook}
                                onBeforeSubmit={onBeforeRebookSubmit}
                                getDetectedWarnings={getDetectedWarnings}
                                rebookFromAppointment={reason ? { reasonCode: reason, note } : undefined}
                                onSuccess={async () => {
                                    await onSubmit();
                                }}
                                customButtonStyle="bg-green-600 hover:bg-green-700 text-white"
                                customButtonContent="Absagen und neu buchen"
                            />
                        </AlertDialogAction>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

type ConfirmCancellationAppointmentButtonProps = {
    onSubmit: () => void | Promise<void>
}
const ConfirmCancellationAppointmentButton = ({ onSubmit }: ConfirmCancellationAppointmentButtonProps) => {
    const { appointmentId, customer, messageFromCustomer, messageToCustomer, appointmentNotes, steps, notifyCustomer, vouchers, status, mode, startDate, initialStartDateTime, setCustomer, setMessageFromCustomer, setMessageToCustomer, setAppointmentNotes, setNotifyCustomer, setVouchers, setStatus, reset } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            appointmentId: state.appointmentId,
            customer: state.customer,
            messageFromCustomer: state.messageFromCustomer,
            messageToCustomer: state.messageToCustomer,
            appointmentNotes: state.appointmentNotes,
            steps: state.steps,
            notifyCustomer: state.notifyCustomer,
            vouchers: state.vouchers,
            status: state.status,
            mode: state.mode,
            startDate: state.startDate,
            initialStartDateTime: state.initialStartDateTime,
            setCustomer: state.setCustomer,
            setMessageFromCustomer: state.setMessageFromCustomer,
            setMessageToCustomer: state.setMessageToCustomer,
            setAppointmentNotes: state.setAppointmentNotes,
            setNotifyCustomer: state.setNotifyCustomer,
            setVouchers: state.setVouchers,
            setStatus: state.setStatus,

            reset: state.reset,
        }))
    );

    const [reason, setReason] = useState<AppointmentCancellationReason | undefined>(undefined);
    const [note, setNote] = useState<string>("");

    // reason/note are now passed directly into modifyAppointment and logged server-side via event listener

    const isLateCancellation = isWithinLateThreshold(initialStartDateTime, startDate.getTimezone());

    const isOtherSelected = reason === AppointmentCancellationReason.OTHER;
    const isNoteValid = !isOtherSelected || isReasonNoteFilled(note);
    const canConfirm = !!reason && isNoteValid;

    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button disabled={!customer || steps.length === 0}>
                    {mode === 'create' ? 'Termin erstellen' : 'Speichern'}
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Absage bestätigen (Grundabfrage)</AlertDialogTitle>
                    <AlertDialogDescription>
                        Bist du dir sicher, dass du den Termin absagen möchtest? Bitte gib den Grund an.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-4">
                    {isLateCancellation && (
                        <div className="flex items-start gap-2 rounded-md border border-red-400 bg-red-100 p-3 text-sm text-red-800">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <p>
                                Der Termin beginnt in weniger als {LATE_THRESHOLD_HOURS} Stunden. Es handelt sich um eine
                                kurzfristige Absage, für die wir laut unseren AGB eine Ausfallentschädigung erheben können.
                            </p>
                        </div>
                    )}

                    <ReasonSelect
                        label="Grund für die Absage*"
                        options={CANCELLATION_REASON_OPTIONS}
                        value={reason}
                        onChange={setReason}
                    />

                    <ReasonNoteField
                        value={note}
                        onChange={setNote}
                        required={isOtherSelected}
                        requiredPlaceholder="Grund für die Absage eingeben..."
                        optionalPlaceholder="Weitere Details zur Absage..."
                    />
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                        asChild
                        disabled={!canConfirm}
                    >
                        <CreateOrModifyAppointmentFormSubmitButton
                            canSubmit={() => canConfirm}
                            cancellationReason={reason}
                            cancellationNote={note}
                            onSuccess={async () => {
                                await onSubmit();
                            }}
                        />
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

type CreateOrModifyAppointmentFormProps = {
    onCancel: () => void;
    onSubmit: () => void;
};

export function CreateOrModifyAppointmentForm({ onCancel, onSubmit }: CreateOrModifyAppointmentFormProps) {
    const { appointmentId, customer, messageFromCustomer, messageToCustomer, appointmentNotes, steps, notifyCustomer, vouchers, status, type, influencerType, mode, startDate, startTime, initialStartDateTime, setCustomer, setMessageFromCustomer, setMessageToCustomer, setAppointmentNotes, setNotifyCustomer, setVouchers, setStatus, setType, setInfluencerType, reset } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            appointmentId: state.appointmentId,
            customer: state.customer,
            messageFromCustomer: state.messageFromCustomer,
            messageToCustomer: state.messageToCustomer,
            appointmentNotes: state.appointmentNotes,
            steps: state.steps,
            notifyCustomer: state.notifyCustomer,
            vouchers: state.vouchers,
            status: state.status,
            type: state.type,
            influencerType: state.influencerType,
            mode: state.mode,
            startDate: state.startDate,
            startTime: state.startTime,
            initialStartDateTime: state.initialStartDateTime,
            setCustomer: state.setCustomer,
            setMessageFromCustomer: state.setMessageFromCustomer,
            setMessageToCustomer: state.setMessageToCustomer,
            setAppointmentNotes: state.setAppointmentNotes,
            setNotifyCustomer: state.setNotifyCustomer,
            setVouchers: state.setVouchers,
            setStatus: state.setStatus,
            setType: state.setType,
            setInfluencerType: state.setInfluencerType,

            reset: state.reset,
        }))
    );

    const [searchQuery, setSearchQuery] = useState('');
    const [initialStatus, setInitialStatus] = useState<AppointmentStatus | null>(null);
    const [showAllAppointments, setShowAllAppointments] = useState<boolean>(true);
    const [discountCodeSearchValue, setDiscountCodeSearchValue] = useState('');
    const [discountCodeSelectedValue, setDiscountCodeSelectedValue] = useState('');
    const [discountWarningDialogOpen, setDiscountWarningDialogOpen] = useState(false);
    const [discountWarningMessage, setDiscountWarningMessage] = useState('');
    const [pendingDiscountCodeName, setPendingDiscountCodeName] = useState<string | null>(null);

    // State für Warning Dialog
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [stepWarnings, setStepWarnings] = useState<{ stepIndex: number; stepName: string; warnings: string[] }[]>([]);
    const [forceSubmit, setForceSubmit] = useState(false); // Flag zum Überspringen der Warnings
    const [rebookPending, setRebookPending] = useState(false);

    const [moveReason, setMoveReason] = useState<AppointmentCancellationReason | undefined>(undefined);
    const [moveNote, setMoveNote] = useState<string>('');

    const currentStartDateTime = `${startDate.format('yyyy-MM-dd')} ${startTime}`;
    const isStartTimeChanged = mode === 'modify' && !!initialStartDateTime && currentStartDateTime !== initialStartDateTime;

    useEffect(() => {
        if (!initialStatus) {
            setInitialStatus(status);
        }
    }, [status]);

    const { data: searchResults, isLoading: isSearching } = trpc.customers.search.useQuery(
        { query: searchQuery, limit: 5 },
        { enabled: searchQuery.length > 1 }
    );

    const selectedCustomerId = customer?.id;
    const {
        data: detailedCustomerData,
        isLoading: isLoadingHistory,
        error: historyError,
        isFetching: isFetchingHistory,
    } = trpc.customers.getOne.useQuery(
        { id: selectedCustomerId! },
        {
            enabled: typeof selectedCustomerId === 'number' && !isNaN(selectedCustomerId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );

    const pastAppointments = useMemo(() => detailedCustomerData?.data?.pastAppointments ?? [], [detailedCustomerData?.data?.pastAppointments]);
    const upcomingAppointments = useMemo(() => detailedCustomerData?.data?.upcomingAppointments ?? [], [detailedCustomerData?.data?.upcomingAppointments]);

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const treatmentDate = startDate.format('yyyy-MM-dd');
    const { data: validDiscountCodes } = trpc.billingDrafts.getValidDiscountCodes.useQuery(
        { treatmentDate },
        { enabled: !!treatmentDate }
    );

    const discountCodeOptions = useMemo(() => {
        if (!validDiscountCodes) return [];
        const currentVoucher = vouchers?.trim().toUpperCase() || '';
        const currentStoreId = selectedStore?.id ?? null;
        return validDiscountCodes
            .filter((code) => {
                if (currentVoucher && code.name.toUpperCase() === currentVoucher) return false;
                // Hard-filter out codes that are scoped to specific stores which
                // do not include the currently selected store.
                if (!isDiscountValidForStore(code.validStoreIds, currentStoreId)) return false;
                if (!discountCodeSearchValue) return true;
                const search = discountCodeSearchValue.toLowerCase();
                return code.name.toLowerCase().includes(search) || code.description.toLowerCase().includes(search);
            })
            .map((code) => ({
                label: code.name,
                value: code.id,
                renderItem: (
                    <div className='w-full flex justify-between items-center'>
                        <div className='flex flex-col'>
                            {code.description && (
                                <span className='text-xs text-muted-foreground'>{code.description}</span>
                            )}
                            <span>{code.name}</span>
                        </div>
                        <span className='font-mono text-sm text-muted-foreground ml-4 shrink-0'>
                            {code.type === 'PERCENTAGE_DISCOUNT'
                                ? `${code.discountValue}%`
                                : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(code.discountValue)}
                        </span>
                    </div>
                ),
            }));
    }, [validDiscountCodes, discountCodeSearchValue, vouchers, selectedStore]);

    const appointmentStartTimeISO = useMemo(() => {
        const ldt = new LocalDateTime(`${startDate.format('yyyy-MM-dd')} ${startTime}`, 'Europe/Berlin');
        return ldt.toUTC().toISOString();
    }, [startDate, startTime]);

    const handleApplyDiscountCode = useCallback((codeId: string) => {
        const code = validDiscountCodes?.find((c) => c.id === codeId);
        if (!code) return;

        const warnings = getDiscountCodeWarnings(code, appointmentStartTimeISO, null);
        if (warnings.length > 0) {
            setPendingDiscountCodeName(code.name);
            setDiscountWarningMessage(formatDiscountCodeWarningMessage(code.name, warnings));
            setDiscountWarningDialogOpen(true);
            setDiscountCodeSearchValue('');
            setDiscountCodeSelectedValue('');
            return;
        }

        setVouchers(code.name);
        setDiscountCodeSearchValue('');
        setDiscountCodeSelectedValue('');
    }, [validDiscountCodes, setVouchers, appointmentStartTimeISO]);

    const handleCustomerSelect = (customerIdStr: string) => {
        const customerId = parseInt(customerIdStr, 10);
        if (isNaN(customerId)) {
            setCustomer(null);
            return;
        }

        const foundCustomer =
            searchResults?.data?.find((c) => c.id === customerId) ||
            (detailedCustomerData?.data?.id === customerId ? detailedCustomerData.data : undefined);


        if (foundCustomer) {
            setCustomer({
                id: foundCustomer.id,
                firstName: foundCustomer.firstName,
                lastName: foundCustomer.lastName,
                email: foundCustomer.email,
                phone: foundCustomer.phone,
                birthday: foundCustomer.birthday,
                city: foundCustomer.city || undefined,
                zipCode: foundCustomer.zipCode || undefined,
                country: foundCustomer.country,
                street: foundCustomer.street || undefined,
                streetNumber: foundCustomer.streetNumber || undefined,
                preferredCommunicationType: foundCustomer.preferredCommunicationType,
                category: foundCustomer.category,
                alternativePhone: foundCustomer.alternativePhone || undefined,
                note: foundCustomer.note || undefined,
                praxismanagerId: 'praxismanagerId' in foundCustomer ? (foundCustomer as any).praxismanagerId : undefined,
            });
        } else {
            setCustomer({ id: customerId, firstName: 'Loading...', lastName: '', email: '', phone: '', country: '', preferredCommunicationType: '', category: '', birthday: null, });
        }
    };

    const customerItems = useMemo(() => {
        const itemsFromSearch = searchResults?.data?.map((c) => ({
            value: c.id.toString(),
            label: `${c.firstName} ${c.lastName} (${c.email})`,
        })) || [];

        if (customer && !itemsFromSearch.some(item => item.value === customer.id.toString())) {
            return [...itemsFromSearch, { value: customer.id.toString(), label: `${customer.firstName} ${customer.lastName} (${customer.email})` }];
        }

        return itemsFromSearch;
    }, [searchResults?.data, customer]);

    const [appointmentLogsModalOpen, setAppointmentLogsModalOpen] = useState<boolean>(false);
    const [cartModalOpen, setCartModalOpen] = useState<boolean>(false);

    const { role } = useUserStore(useShallow((s) => ({ role: s.user?.role })));
    const isAdmin = role === UserRole.ADMIN;

    const customerAge = useMemo(() => {
        if (!customer) return null;
        if (!customer.birthday) return null;
        return `${Math.floor(
            (Date.now() - new Date(customer.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365.25)
        )}`;
    }, [customer]);

    const checkAllStepsForWarnings = useCallback(() => {
        const allWarnings: { stepIndex: number; stepName: string; warnings: string[] }[] = [];

        steps.forEach((step, index) => {
            const warnings: string[] = [];

            // Check practitioners
            step.practitioners.forEach((practitioner) => {
                const selectableResource = step.selectablePractitioners.find((s) => s.id === practitioner.id);
                const isAvailable = selectableResource?.isAvailable === undefined ? true : selectableResource.isAvailable;
                const isShiftAvailable = selectableResource && 'isShiftAvailable' in selectableResource ? selectableResource.isShiftAvailable as Boolean : true;
                const isBlocked = selectableResource && 'isBlocked' in selectableResource ? selectableResource.isBlocked as Boolean : false;
                const numberOfExistingBookings = selectableResource?.numberOfExistingBookings ?? 0;
                const resourceAlreadyAssignedToThisStep = step.initialPractitioners.some((r) => r.id === practitioner.id);
                const isBusy = isBlocked === true || !isShiftAvailable ||
                    (numberOfExistingBookings === 0 && !isAvailable) ||
                    (numberOfExistingBookings === 1 && !isAvailable && !resourceAlreadyAssignedToThisStep) ||
                    (numberOfExistingBookings > 1 && !isAvailable);
                const canPerform = 'canPerform' in practitioner ? practitioner.canPerform as Boolean : true;

                const isResourceRequired = step.acceptablePractitioners && step.acceptablePractitioners.length > 0;

                if (!isResourceRequired) {
                    warnings.push(`Behandler "${practitioner.name}" ist eine unnötige Ressource`);
                } else if (isBusy || !canPerform) {
                    warnings.push(`Behandler "${practitioner.name}" ist ${!canPerform ? 'nicht zugelassen' : 'nicht verfügbar'}`);
                }
            });

            // Check if required practitioners are missing
            if (step.acceptablePractitioners && step.acceptablePractitioners.length > 0) {
                if (step.practitioners.length === 0) {
                    const categoryNames = step.acceptablePractitioners
                        .map(constraint => {
                            // Try to find the category name from selectablePractitioners
                            const samplePractitioner = step.selectablePractitioners.find(
                                p => p.categoryId === constraint.categoryId
                            );
                            return samplePractitioner?.categoryName || `Kategorie ${constraint.categoryId}`;
                        })
                        .join(', ');
                    warnings.push(`⚠️ FEHLT: Mindestens ein Behandler erforderlich (${categoryNames})`);
                }
            }

            // Check rooms
            step.rooms.forEach((room) => {
                const selectableResource = step.selectableRooms.find((s) => s.id === room.id);
                const isAvailable = selectableResource?.isAvailable === undefined ? true : selectableResource.isAvailable;
                const isShiftAvailable = selectableResource && 'isShiftAvailable' in selectableResource ? selectableResource.isShiftAvailable as Boolean : true;
                const isBlocked = selectableResource && 'isBlocked' in selectableResource ? selectableResource.isBlocked as Boolean : false;
                const numberOfExistingBookings = selectableResource?.numberOfExistingBookings ?? 0;
                const resourceAlreadyAssignedToThisStep = step.initialRooms.some((r) => r.id === room.id);
                const isBusy = isBlocked === true || !isShiftAvailable ||
                    (numberOfExistingBookings === 0 && !isAvailable) ||
                    (numberOfExistingBookings === 1 && !isAvailable && !resourceAlreadyAssignedToThisStep) ||
                    (numberOfExistingBookings > 1 && !isAvailable);
                const canPerform = 'canPerform' in room ? room.canPerform as Boolean : true;

                const isResourceRequired = step.acceptableRooms && step.acceptableRooms.length > 0;

                if (!isResourceRequired) {
                    warnings.push(`Raum "${room.name}" ist eine unnötige Ressource`);
                } else if (isBusy || !canPerform) {
                    warnings.push(`Raum "${room.name}" ist ${!canPerform ? 'nicht zugelassen' : 'nicht verfügbar'}`);
                }
            });

            // Check if required rooms are missing
            if (step.acceptableRooms && step.acceptableRooms.length > 0) {
                if (step.rooms.length === 0) {
                    const categoryNames = step.acceptableRooms
                        .map(constraint => {
                            // Try to find the category name from selectableRooms
                            const sampleRoom = step.selectableRooms.find(
                                r => r.categoryId === constraint.categoryId
                            );
                            return sampleRoom?.categoryName || `Kategorie ${constraint.categoryId}`;
                        })
                        .join(', ');
                    warnings.push(`⚠️ FEHLT: Mindestens ein Raum erforderlich (${categoryNames})`);
                }
            }

            // Check equipment
            step.equipment.forEach((equip) => {
                const selectableResource = step.selectableEquipment.find((s) => s.id === equip.id);
                const isAvailable = selectableResource?.isAvailable === undefined ? true : selectableResource.isAvailable;
                const isShiftAvailable = selectableResource && 'isShiftAvailable' in selectableResource ? selectableResource.isShiftAvailable as Boolean : true;
                const isBlocked = selectableResource && 'isBlocked' in selectableResource ? selectableResource.isBlocked as Boolean : false;
                const numberOfExistingBookings = selectableResource?.numberOfExistingBookings ?? 0;
                const resourceAlreadyAssignedToThisStep = step.initialEquipment.some((r) => r.id === equip.id);
                const isBusy = isBlocked === true || !isShiftAvailable ||
                    (numberOfExistingBookings === 0 && !isAvailable) ||
                    (numberOfExistingBookings === 1 && !isAvailable && !resourceAlreadyAssignedToThisStep) ||
                    (numberOfExistingBookings > 1 && !isAvailable);
                const canPerform = 'canPerform' in equip ? equip.canPerform as Boolean : true;

                const isResourceRequired = step.acceptableEquipments && step.acceptableEquipments.length > 0;

                if (!isResourceRequired) {
                    warnings.push(`Gerät "${equip.name}" ist eine unnötige Ressource`);
                } else if (isBusy || !canPerform) {
                    warnings.push(`Gerät "${equip.name}" ist ${!canPerform ? 'nicht zugelassen' : 'nicht verfügbar'}`);
                }
            });

            // Check if required equipment is missing
            if (step.acceptableEquipments && step.acceptableEquipments.length > 0) {
                if (step.equipment.length === 0) {
                    const categoryNames = step.acceptableEquipments
                        .map(constraint => {
                            // Try to find the category name from selectableEquipment
                            const sampleEquipment = step.selectableEquipment.find(
                                e => e.categoryId === constraint.categoryId
                            );
                            return sampleEquipment?.categoryName || `Kategorie ${constraint.categoryId}`;
                        })
                        .join(', ');
                    warnings.push(`⚠️ FEHLT: Mindestens ein Gerät erforderlich (${categoryNames})`);
                }
            }

            if (warnings.length > 0) {
                allWarnings.push({
                    stepIndex: index + 1,
                    stepName: step.name,
                    warnings: warnings,
                });
            }
        });

        return allWarnings;
    }, [steps]);

    // Flat, log-friendly version of the warnings so they can be persisted with the
    // user action log when the user saves anyway.
    const getDetectedWarnings = useCallback(() => {
        return checkAllStepsForWarnings().flatMap((stepWarning) =>
            stepWarning.warnings.map((warning) => `Schritt ${stepWarning.stepIndex} (${stepWarning.stepName}): ${warning}`)
        );
    }, [checkAllStepsForWarnings]);

    const handleBeforeSubmit = () => {
        if (forceSubmit) {
            setForceSubmit(false); // Reset flag
            return true; // Skip warning check
        }

        const warnings = checkAllStepsForWarnings();

        if (warnings.length > 0) {
            setStepWarnings(warnings);
            setRebookPending(false);
            setShowWarningDialog(true);
            return false; // Prevent submit
        }

        return true; // Allow submit
    };

    // Same as handleBeforeSubmit, but the warning dialog's force submit then also rebooks instead of moving.
    const handleBeforeRebookSubmit = () => {
        const canProceed = handleBeforeSubmit();
        setRebookPending(!canProceed);
        return canProceed;
    };

    const handleSubmitSuccess = () => {
        setShowWarningDialog(false);
        setForceSubmit(false);
        setRebookPending(false);
        setInitialStatus(null);
        setMoveReason(undefined);
        setMoveNote('');
        reset();
        onSubmit();
    };

    return (
        <div className='space-y-6'>
            <div className='space-y-2'>
                <div className='flex flex-row justify-between gap-2'>
                    <Label htmlFor='customer'>Kunde</Label>
                    <CreateCustomerButtonAndModal />
                </div>
                <AutoComplete
                    selectedValue={customer?.id.toString() || ''}
                    onSelectedValueChange={handleCustomerSelect}
                    searchValue={searchQuery}
                    onSearchValueChange={setSearchQuery}
                    items={
                        customer?.id
                            ? [...customerItems, { value: customer?.id.toString(), label: `${customer.firstName} ${customer.lastName} (${customer.email})` }]
                            : customerItems
                    }
                    isLoading={searchQuery.length > 2 && !searchResults}
                    emptyMessage='Kein Kunde gefunden'
                    placeholder='Kunde auswählen...'
                />
                <div className='py-2'>
                    <SelectedBookableItemsList />
                </div>
                {customer && (
                    <div className='mt-2 space-y-4'>
                        <div className='flex flex-col gap-2 p-3 bg-accent rounded-md'>
                            <div className='grid grid-cols-1 gap-1 text-sm'>
                                <div className='flex items-center gap-2 cursor-pointer'
                                    onClick={() => {
                                        window.open(`/customer/${customer.id}`)
                                    }}
                                >
                                    <User className='h-3 w-3 text-muted-foreground flex-shrink-0' />
                                    <span className='text-blue-600 underline hover:text-blue-800'>
                                        {customer.firstName} {customer.lastName}
                                    </span>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <Mail className='h-3 w-3 text-muted-foreground flex-shrink-0' />
                                    <span>{customer.email}</span>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <Clock className='h-3 w-3 text-muted-foreground flex-shrink-0' />
                                    <span>{!customerAge ? '-' : `${customerAge} J.`}</span>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <Phone className='h-3 w-3 text-muted-foreground flex-shrink-0' />
                                    <span>{customer.phone}</span>
                                    {customer.alternativePhone && <span className='text-xs text-muted-foreground ml-2'>Alt: {customer.alternativePhone}</span>}
                                </div>
                                {(customer.street || customer.city) && (
                                    <div className='flex items-center gap-2'>
                                        <MapPin className='h-3 w-3 text-muted-foreground flex-shrink-0' />
                                        <span>
                                            {customer.street && customer.streetNumber && `${customer.street} ${customer.streetNumber}, `}
                                            {customer.zipCode && `${customer.zipCode} `}
                                            {customer.city && customer.city}
                                            {customer.country && `, ${customer.country}`}
                                        </span>
                                    </div>
                                )}
                                <div className='flex items-start gap-2 mt-1'>
                                    <StickyNote className='h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0' />
                                    <div className={
                                        `text-xs italic ${customer.note ? 'text-red-500 font-bold' : 'text-muted-foreground'}`
                                    } style={{ whiteSpace: 'pre-wrap' }}>
                                        {customer.note || 'Keine Notiz vorhanden...'}
                                    </div>
                                </div>
                            </div>
                            <Separator />
                            <div className='space-y-2'>
                                <div className='flex items-center justify-between'>
                                    <Label className='text-xs font-semibold'>Filter: Nur SCHEDULED und COMPLETED Termine anzeigen</Label>
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            id="show-all-appointments"
                                            checked={!showAllAppointments}
                                            onCheckedChange={(checked) => setShowAllAppointments(!checked)}
                                            className='data-[state="checked"]:!bg-primary-400'
                                        />
                                    </div>
                                </div>
                            </div>
                            <Separator />
                            <div className='space-y-2'>
                                <Label className='text-xs font-semibold'>Bevorstehende Termine</Label>
                                {(isLoadingHistory || (isFetchingHistory && upcomingAppointments.length === 0)) && (
                                    <div className="flex justify-center items-center h-20">
                                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                                {historyError && !isLoadingHistory && (
                                    <Alert variant="destructive" className="text-xs py-2 px-3">
                                        <AlertTitle className="text-xs">Error</AlertTitle>
                                        <AlertDescription className="text-xs">
                                            Terminehistorie konnte nicht geladen werden. {historyError.message}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {!isLoadingHistory && !historyError && (
                                    <div className='overflow-y-auto max-h-[200px] border rounded-md relative'>
                                        {isFetchingHistory && upcomingAppointments.length > 0 && (
                                            <div className="absolute top-1 right-1 p-1 bg-background/70 rounded-full">
                                                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                            </div>
                                        )}
                                        <CustomerAppointmentList appointments={upcomingAppointments
                                            .filter(appointment =>
                                                showAllAppointments ||
                                                appointment.status === AppointmentStatus.SCHEDULED ||
                                                appointment.status === AppointmentStatus.COMPLETED
                                            )
                                            .map((appointment) => ({
                                                ...appointment,
                                                steps: appointment.steps.map((step) => ({
                                                    ...step,
                                                    dateFromLocal: LocalDateTime.deserialize(step.dateFromLocalSerialized),
                                                    dateUntilLocal: LocalDateTime.deserialize(step.dateUntilLocalSerialized),
                                                }))
                                            }))}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className='space-y-2'>
                                <Label className='text-xs font-semibold'>Vergangene Termine</Label>
                                {(isLoadingHistory || (isFetchingHistory && pastAppointments.length === 0)) && (
                                    <div className="flex justify-center items-center h-20">
                                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                                {historyError && !isLoadingHistory && (
                                    <Alert variant="destructive" className="text-xs py-2 px-3">
                                        <AlertTitle className="text-xs">Error</AlertTitle>
                                        <AlertDescription className="text-xs">
                                            Terminehistorie konnte nicht geladen werden. {historyError.message}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {!isLoadingHistory && !historyError && (
                                    <div className='overflow-y-auto max-h-[200px] border rounded-md relative'>
                                        {isFetchingHistory && pastAppointments.length > 0 && (
                                            <div className="absolute top-1 right-1 p-1 bg-background/70 rounded-full">
                                                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                            </div>
                                        )}
                                        <CustomerAppointmentList appointments={pastAppointments
                                            .filter(appointment =>
                                                showAllAppointments ||
                                                appointment.status === AppointmentStatus.SCHEDULED ||
                                                appointment.status === AppointmentStatus.COMPLETED
                                            )
                                            .map((appointment) => ({
                                                ...appointment,
                                                steps: appointment.steps.map((step) => ({
                                                    ...step,
                                                    dateFromLocal: LocalDateTime.deserialize(step.dateFromLocalSerialized),
                                                    dateUntilLocal: LocalDateTime.deserialize(step.dateUntilLocalSerialized),
                                                }))
                                            }))}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {messageFromCustomer && (
                <div className='space-y-2 mt-4'>
                    <div className='flex flex-col gap-1'>
                        <Label htmlFor='messageFromCustomer'>Nachricht vom Kunden</Label>
                        <span className='text-xs text-muted-foreground'>Nachrichten-Feld aus Online-Terminbuchung.</span>
                    </div>
                    <Textarea
                        id='messageFromCustomer'
                        placeholder='Keine Nachricht vorhanden...'
                        value={messageFromCustomer}
                        disabled
                        className='min-h-[100px] !placeholder-primary-400 font-medium bg-yellow-200 text-black'
                        style={{ whiteSpace: 'pre-wrap' }}
                    />
                </div>
            )}

            {appointmentNotes && (
                <div className='space-y-2 mt-4'>
                    <div className='flex flex-col gap-1'>
                        <Label htmlFor='appointmentNotesPreview'>Termin-Notiz (intern)</Label>
                        <span className='text-xs text-muted-foreground'>Interne Notiz zu diesem Termin (bearbeitbar weiter unten).</span>
                    </div>
                    <Textarea
                        id='appointmentNotesPreview'
                        placeholder='Keine Notiz vorhanden...'
                        value={appointmentNotes}
                        disabled
                        className='min-h-[100px] !placeholder-primary-400 font-medium bg-blue-100 text-black'
                        style={{ whiteSpace: 'pre-wrap' }}
                    />
                </div>
            )}

            <div>
                <AppointmentBuilder />
            </div>

            <div className='space-y-2'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='messageToCustomer'>Nachricht senden</Label>
                    <span className='text-xs text-muted-foreground'>Nachricht an den Kunden (wird per E-Mail versendet).</span>
                </div>
                <Textarea
                    id='messageToCustomer'
                    placeholder='Text hier einfügen...'
                    value={messageToCustomer}
                    onChange={(e) => setMessageToCustomer(e.target.value)}
                    className='min-h-[100px] !placeholder-primary-400 text-primary-500 font-medium'
                />
            </div>

            <div className='space-y-2'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='appointmentNotes'>Termin-Notiz (intern)</Label>
                    <span className='text-xs text-muted-foreground'>Notizen zu diesem Termin (Kunden sehen diese nicht).</span>
                </div>
                <Textarea
                    id='appointmentNotes'
                    placeholder='Text hier einfügen...'
                    value={appointmentNotes}
                    onChange={(e) => setAppointmentNotes(e.target.value)}
                    className='min-h-[100px] !placeholder-primary-400 text-primary-500 font-medium'
                />
            </div>

            <div className='space-y-2'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='vouchers'>Gutscheine / Discount Code</Label>
                    <span className='text-xs text-muted-foreground'>Rabattcode für diesen Termin auswählen oder manuell eingeben.</span>
                </div>
                <AutoComplete
                    selectedValue={discountCodeSelectedValue}
                    onSelectedValueChange={(value) => {
                        setDiscountCodeSelectedValue(value);
                        handleApplyDiscountCode(value);
                        setDiscountCodeSelectedValue('');
                    }}
                    searchValue={discountCodeSearchValue}
                    onSearchValueChange={setDiscountCodeSearchValue}
                    items={discountCodeOptions}
                    placeholder='Rabattcode suchen...'
                    emptyMessage='Keine Rabattcodes verfügbar'
                    resetSearchOnSelectItem
                />
                {vouchers && vouchers.trim().length > 0 && (
                    <div className='flex items-center gap-2 px-3 py-2 rounded-md bg-green-50 border border-green-200'>
                        <span className='text-sm font-medium text-green-800'>Angewendet: {vouchers}</span>
                        <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            className='h-5 w-5 p-0 text-green-600 hover:text-red-600'
                            onClick={() => setVouchers('')}
                        >
                            <X className='h-3 w-3' />
                        </Button>
                    </div>
                )}
            </div>

            <div className='space-y-2'>
                <Label htmlFor='status'>Status</Label>
                <Select value={status?.toString() || undefined} onValueChange={(val) => setStatus(val as AppointmentStatus)}>
                    <SelectTrigger id='status' className='w-full'>
                        <SelectValue placeholder='Status auswählen' />
                    </SelectTrigger>
                    <SelectContent>
                        {Object.values(AppointmentStatus).map((appointmentStatus) => (
                            <SelectItem key={appointmentStatus} value={appointmentStatus}>
                                {getAppointmentStatusLabel(appointmentStatus)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex items-center space-x-2">
                <Switch
                    id="notifyCustomer"
                    checked={notifyCustomer}
                    onCheckedChange={setNotifyCustomer}
                    className='data-[state="checked"]:!bg-primary-400'
                />
                <Label htmlFor="notifyCustomer">Benachrichtigungen an Kunden senden</Label>
            </div>

            <div className='py-4'>
                <AppointmentColorPicker />
            </div>

            <div className='flex justify-end gap-2 mt-6'>
                <Button
                    variant='outline'
                    onClick={() => {
                        setInitialStatus(null);
                        reset();
                        onCancel();
                    }}
                >
                    Abbrechen
                </Button>
                {initialStatus !== AppointmentStatus.CANCELLED && status === AppointmentStatus.CANCELLED ? (
                    <ConfirmCancellationAppointmentButton
                        onSubmit={() => {
                            setInitialStatus(null);
                            reset();
                            onSubmit();
                        }}
                    />
                ) : initialStatus !== AppointmentStatus.DECLINED && status === AppointmentStatus.DECLINED ? (
                    <ConfirmDeclinedAppointmentButton
                        onSubmit={() => {
                            setInitialStatus(null);
                            reset();
                            onSubmit();
                        }}
                    />
                ) : isStartTimeChanged ? (
                    <ConfirmMoveAppointmentButton
                        previousStart={formatStartForDisplay(initialStartDateTime!)}
                        newStart={formatStartForDisplay(currentStartDateTime)}
                        isLateMove={isWithinLateThreshold(initialStartDateTime, startDate.getTimezone())}
                        reason={moveReason}
                        onReasonChange={setMoveReason}
                        note={moveNote}
                        onNoteChange={setMoveNote}
                        onBeforeSubmit={handleBeforeSubmit}
                        onBeforeRebookSubmit={handleBeforeRebookSubmit}
                        getDetectedWarnings={getDetectedWarnings}
                        onSubmit={handleSubmitSuccess}
                    />
                ) : (
                    <CreateOrModifyAppointmentFormSubmitButton
                        onBeforeSubmit={handleBeforeSubmit}
                        getDetectedWarnings={getDetectedWarnings}
                        onSuccess={handleSubmitSuccess}
                    />
                )}
            </div>

            {/* Warning Dialog */}
            <AlertDialog
                open={showWarningDialog}
                onOpenChange={(open) => {
                    setShowWarningDialog(open);
                    if (!open) {
                        setRebookPending(false);
                    }
                }}
            >
                <AlertDialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            Ressourcen-Warnungen
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base pt-4">
                            Die folgenden Ressourcen-Warnungen wurden gefunden:

                            <div className="mt-3 space-y-4">
                                {stepWarnings.map((stepWarning, index) => (
                                    <div key={index} className="border-l-4 border-yellow-500 pl-3">
                                        <p className="font-semibold text-foreground">
                                            Schritt {stepWarning.stepIndex}: {stepWarning.stepName}
                                        </p>
                                        <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                                            {stepWarning.warnings.map((warning, wIndex) => (
                                                <li key={wIndex} className="text-destructive">{warning}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            <p className="mt-4 font-medium">
                                Möchtest du trotzdem fortfahren?
                            </p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row justify-between gap-3">
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <CreateOrModifyAppointmentFormSubmitButton
                            onBeforeSubmit={() => {
                                setForceSubmit(true);
                                return true; // Force submit
                            }}
                            getDetectedWarnings={getDetectedWarnings}
                            cancellationReason={isStartTimeChanged ? moveReason : undefined}
                            cancellationNote={isStartTimeChanged ? moveNote : undefined}
                            rebookFromAppointment={rebookPending && moveReason ? { reasonCode: moveReason, note: moveNote } : undefined}
                            onSuccess={handleSubmitSuccess}
                            customButtonStyle="bg-red-600 hover:bg-red-700 text-white"
                            customButtonContent={
                                <>
                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                    Trotzdem speichern
                                </>
                            }
                        />
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {!!appointmentId && (
                <>
                    <AppointmentLogsModal
                        appointmentId={appointmentId}
                        open={appointmentLogsModalOpen}
                        onClose={() => setAppointmentLogsModalOpen(false)}
                    />
                    {isAdmin && (
                        <AppointmentCartModal
                            appointmentId={appointmentId}
                            open={cartModalOpen}
                            onClose={() => setCartModalOpen(false)}
                        />
                    )}
                    <span className='text-xs text-gray-700'>Appointment id: {appointmentId}</span>
                    <div className='flex items-center gap-1'>
                        <Button onClick={() => setAppointmentLogsModalOpen(true)} variant='ghost'><Book className="h-4 w-4" />View logs</Button>
                        {isAdmin && (
                            <Button onClick={() => setCartModalOpen(true)} variant='ghost' size='icon' title='Warenkorb anzeigen'>
                                <ShoppingCart className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </>
            )}

            <div className='flex items-center gap-3 mt-4'>
                <Label className='text-sm text-muted-foreground whitespace-nowrap'>Typ:</Label>
                <Select
                    value={type}
                    onValueChange={(v) => {
                        setType(v);
                        if (v !== 'INFLUENCER') {
                            setInfluencerType(null);
                        }
                    }}
                >
                    <SelectTrigger className='w-64 h-8'>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value='NORMAL'>Standard</SelectItem>
                        <SelectItem value='INFLUENCER'>Influencer</SelectItem>
                        <SelectItem value='MARKETING'>Marketing</SelectItem>
                        <SelectItem value='ACADEMY'>Academy</SelectItem>
                        <SelectItem value='KULANZ'>Kulanz/Beschwerde</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {type === 'INFLUENCER' && (
                <div className='flex items-center gap-3 mt-4'>
                    <Label className='text-sm text-muted-foreground whitespace-nowrap'>Influencer-Typ:</Label>
                    <Select
                        value={influencerType || ''}
                        onValueChange={(v) => setInfluencerType(v || null)}
                    >
                        <SelectTrigger className='w-64 h-8'>
                            <SelectValue placeholder='Typ auswählen...' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='BEAUTY_INSIDER'>Beauty Insider</SelectItem>
                            <SelectItem value='INFLUENCER'>Influencer Normal</SelectItem>
                            <SelectItem value='INFLUENCER_FLATRATE'>Influencer Pauschalversteuerung</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="mt-8 border border-destructive/50 rounded-md p-4">
                <h3 className="text-lg font-medium text-destructive mb-2">Gefährliche Zone</h3>
                <p className="text-sm text-muted-foreground mb-4">
                    Aktionen hier können nicht rückgängig gemacht werden!
                </p>
                <ModifyAppointmentFormDeleteButton
                    onSuccess={() => {
                        reset();
                        onSubmit();
                    }}
                />
            </div>

            <AlertDialog open={discountWarningDialogOpen} onOpenChange={setDiscountWarningDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Rabattcode-Einschränkung</AlertDialogTitle>
                        <AlertDialogDescription className='whitespace-pre-line'>
                            {discountWarningMessage}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => {
                            setPendingDiscountCodeName(null);
                            setDiscountWarningDialogOpen(false);
                            setDiscountWarningMessage('');
                        }}>
                            Abbrechen
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={() => {
                            if (pendingDiscountCodeName) {
                                setVouchers(pendingDiscountCodeName);
                            }
                            setPendingDiscountCodeName(null);
                            setDiscountWarningDialogOpen(false);
                            setDiscountWarningMessage('');
                        }}>
                            Trotzdem anwenden
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const CreateOrModifyAppointmentFormSubmitButton = ({
    canSubmit,
    onBeforeSubmit,
    getDetectedWarnings,
    onSuccess,
    customButtonStyle,
    customButtonContent,
    cancellationReason,
    cancellationNote,
    rebookFromAppointment,
}: {
    canSubmit?: () => boolean;
    onBeforeSubmit?: () => boolean;
    getDetectedWarnings?: () => string[];
    onSuccess: () => void;
    customButtonStyle?: string;
    customButtonContent?: React.ReactNode;
    cancellationReason?: AppointmentCancellationReason;
    cancellationNote?: string;
    /** Instead of modifying, create a new appointment from the form data and then cancel the loaded one. */
    rebookFromAppointment?: RebookFromAppointment;
}) => {
    const { appointmentId, color, status, type, influencerType, mode, customer, messageFromCustomer, messageToCustomer, appointmentNotes, notifyCustomer, vouchers, steps, selectedBookableItems, startDate, startTime } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            appointmentId: state.appointmentId,
            color: state.color,
            status: state.status,
            type: state.type,
            influencerType: state.influencerType,
            mode: state.mode,
            customer: state.customer,
            messageFromCustomer: state.messageFromCustomer,
            messageToCustomer: state.messageToCustomer,
            appointmentNotes: state.appointmentNotes,
            notifyCustomer: state.notifyCustomer,
            vouchers: state.vouchers,
            steps: state.steps,
            selectedBookableItems: state.selectedBookableItems,
            startDate: state.startDate,
            startTime: state.startTime,
        }))
    );

    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone,
        }))
    );

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const scheduleAppointment = trpc.appointments.scheduleAppointment.useMutation();
    const modifyAppointment = trpc.appointments.modifyAppointment.useMutation();
    const updateStatus = trpc.appointments.updateStatus.useMutation();

    const handleSubmit = async () => {
        console.log("handleSubmit");

        // Check if onBeforeSubmit returns false (warnings found)
        if (onBeforeSubmit && !onBeforeSubmit()) {
            return; // Stop submission
        }

        if (!selectedStore?.id || !customer || !status || steps.length === 0) {
            return;
        }

        if (canSubmit !== undefined && !canSubmit()) {
            return;
        }

        // When the appointment type is INFLUENCER, an influencer type must be selected.
        if (type === 'INFLUENCER' && !influencerType) {
            toast.error('Bitte einen Influencer-Typ auswählen.');
            return;
        }

        const appointmentStartTime = new LocalDateTime(`${startDate.format("yyyy-MM-dd")} ${startTime}`, 'Europe/Berlin');

        // Persisted with the user action log so overridden warnings stay traceable.
        // Capped at the limit the server input accepts.
        const detectedWarnings = getDetectedWarnings?.().slice(0, 50);

        const sortedSteps = steps.sort((a, b) => a.minuteStart - b.minuteStart);
        const appointmentSteps = sortedSteps.map((step, index) => {
            const practitioners = step.practitioners.map((practitioner) => ({ id: practitioner.id }));
            const rooms = step.rooms.map((room) => ({ id: room.id }));
            const equipment = step.equipment.map((equip) => ({ id: equip.id }));

            return {
                title: step.name,
                description: step.shortDescription,
                duration: step.minuteEnd - step.minuteStart,
                minuteStart: step.minuteStart,
                minuteEnd: step.minuteEnd,
                originalTreatmentStepId: step.originalTreatmentStepId,
                practitioners: practitioners,
                rooms: rooms,
                equipment: equipment,
            };
        });

        const appointmentTitle = selectedBookableItems.length > 0
            ? selectedBookableItems
                .map((item) => item.addOns.length > 0
                    ? `${item.bookableItemName} (+ ${item.addOns.map((addon) => addon.addOnName).join(', ')})`
                    : item.bookableItemName)
                .join(", ")
            : steps.map((s) => s.name).join(', ');

        if (mode === 'create' || (rebookFromAppointment && appointmentId)) {
            console.log(appointmentStartTime);
            // Map selectedBookableItems to bookableItems format for API
            const bookableItems = selectedBookableItems.map((item) => ({
                bookableItemId: item.bookableItemId,
                bookableItemType: item.bookableItemType,
                treatmentVariantId: item.treatmentVariantId,
                addOns: item.addOns.map((addon) => ({
                    addOnId: addon.addOnId,
                    variantId: addon.variantId,
                })),
                ...(item.bundleId && { bundleId: item.bundleId }),
                isBundleRedemption: item.isBundleRedemption ?? false,
            }));
            await scheduleAppointment.mutateAsync({
                storeId: selectedStore.id,
                bookableItems,
                type: type as any,
                influencerType: influencerType as any,
                title: appointmentTitle,
                titleShort: steps[0].name,
                startTime: appointmentStartTime.toJson(),
                color: color,
                customerId: customer.id,
                messageFromCustomer: messageFromCustomer,
                messageToCustomer: messageToCustomer,
                appointmentNotes: appointmentNotes,
                notifyCustomer: rebookFromAppointment ? true : notifyCustomer,
                vouchers: vouchers,
                notifyForEarlierAppointment: false,
                steps: appointmentSteps.map((step) => ({
                    originalTreatmentStepId: step.originalTreatmentStepId || undefined,
                    originalTreatmentVariantId: undefined,
                    name: step.title,
                    shortDescription: step.description,
                    minuteStart: step.minuteStart,
                    minuteEnd: step.minuteEnd,
                    allocatedPractitionerIds: step.practitioners.map((p) => p.id),
                    allocatedRoomIds: step.rooms.map((r) => r.id),
                    allocatedEquipmentIds: step.equipment.map((e) => e.id),
                })),
                detectedWarnings: detectedWarnings,
            });
            if (rebookFromAppointment && appointmentId) {
                try {
                    await updateStatus.mutateAsync({
                        appointmentId,
                        appointmentStatus: AppointmentStatus.CANCELLED,
                        reasonCode: rebookFromAppointment.reasonCode,
                        note: rebookFromAppointment.note,
                        notifyCustomer: true,
                    });
                    toast.success('Alter Termin abgesagt und neuer Termin erstellt.');
                } catch (error) {
                    toast.error(
                        `Der neue Termin wurde erstellt, aber der alte Termin konnte nicht abgesagt werden. Bitte sage ihn manuell ab. (${error instanceof Error ? error.message : 'Unbekannter Fehler'})`,
                        { duration: 15000 }
                    );
                }
            }
            onSuccess();
        } else if (mode === 'modify' && appointmentId) {
            // Map selectedBookableItems to bookableItems format for API
            // Supports both legacy (old appointments) and bookable_item (new CMS items) types
            const bookableItemsForModify = selectedBookableItems.map((item) => {
                if (item.type === 'legacy') {
                    return {
                        type: 'legacy' as const,
                        treatmentVariantId: item.treatmentVariantId,
                    };
                }
                return {
                    type: 'bookable_item' as const,
                    bookableItemId: item.bookableItemId,
                    bookableItemType: item.bookableItemType,
                    treatmentVariantId: item.treatmentVariantId,
                    addOns: item.addOns.map((addon) => ({
                        addOnId: addon.addOnId,
                        variantId: addon.variantId,
                    })),
                    ...(item.bundleId && { bundleId: item.bundleId }),
                    isBundleRedemption: item.isBundleRedemption ?? false,
                };
            });
            await modifyAppointment.mutateAsync({
                appointmentId: appointmentId,
                status: status,
                type: type as any,
                influencerType: influencerType as any,
                storeId: selectedStore.id,
                bookableItems: bookableItemsForModify,
                title: appointmentTitle,
                titleShort: steps[0].name,
                startTime: appointmentStartTime.toJson(),
                color: color,
                customerId: customer.id,
                messageFromCustomer: messageFromCustomer,
                messageToCustomer: messageToCustomer,
                appointmentNotes: appointmentNotes,
                notifyCustomer: notifyCustomer,
                vouchers: vouchers,
                notifyForEarlierAppointment: false,
                steps: appointmentSteps.map((step) => ({
                    originalTreatmentStepId: step.originalTreatmentStepId || undefined,
                    originalTreatmentVariantId: undefined,
                    name: step.title,
                    shortDescription: step.description,
                    minuteStart: step.minuteStart,
                    minuteEnd: step.minuteEnd,
                    allocatedPractitionerIds: step.practitioners.map((p) => p.id),
                    allocatedRoomIds: step.rooms.map((r) => r.id),
                    allocatedEquipmentIds: step.equipment.map((e) => e.id),
                })),
                detectedWarnings: detectedWarnings,
                modificationReasonCode: cancellationReason,
                modificationNote: cancellationNote,
            });
            onSuccess();
        } else {
            console.log(appointmentId);
            console.log(mode)
        }
    };

    const isPending = scheduleAppointment.isPending || modifyAppointment.isPending || updateStatus.isPending;
    const isDisabled = !customer || steps.length === 0 || isPending;

    return (
        <Button
            onClick={handleSubmit}
            disabled={isDisabled || (!!canSubmit && canSubmit() !== true)}
            className={customButtonStyle}
        >
            {isPending && <Loader2 className='animate-spin' />}
            {customButtonContent || (mode === 'create' ? 'Termin erstellen' : 'Speichern')}
        </Button>
    );
};

export const ModifyAppointmentFormDeleteButton = ({ onSuccess }: { onSuccess: () => void }) => {
    const [open, setOpen] = useState(false);
    const { appointmentId, mode, reset } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            appointmentId: state.appointmentId,
            mode: state.mode,
            reset: state.reset
        }))
    );

    const deleteAppointment = trpc.appointments.archive.useMutation({
        onSuccess: () => {
            toast.success("Appointment deleted successfully");
        },
        onError: (error) => {
            toast.error(`Failed to delete appointment: ${error.message}`);
        }
    });

    const handleDelete = async () => {
        if (!appointmentId) {
            toast.error("No appointment ID found");
            return;
        }

        try {
            await deleteAppointment.mutateAsync({ appointmentId });
            onSuccess();
            setOpen(false);
        } catch (error) {
            // Error is handled by the mutation's onError
        }
    };

    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    if (mode !== 'modify' || !appointmentId || !isAdmin) {
        return null;
    }

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button variant="destructive">
                    {deleteAppointment.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                    )}
                    Termin löschen
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Bist du dir sicher?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Diese Aktion kann nicht rückgängig gemacht werden. Diese Aktion wird den Termin löschen und ihn von der Kalenderansicht entfernen.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            handleDelete();
                        }}
                        disabled={deleteAppointment.isPending}
                    >
                        {deleteAppointment.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Löschen...
                            </>
                        ) : (
                            "Löschen"
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};